// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built target (stand-up or drop). The upstream target loads JSON meshes. This one is a
// slab, or a cylinder for the round type. The origin is the centre of the base on the playfield.
// The front faces +z of the item frame, which is toward the player when rotZ is 0.

import { type BufferGeometry, CylinderGeometry, PlaneGeometry } from "three";
import type { TargetSpec } from "../../shared/contracts.ts";
import { extrudeOutline } from "./shared.ts";

/** The parts of a target. */
export interface TargetGeometry {
  readonly body: BufferGeometry;
  /** A flat square on the front for the label. Null when the target has no label. */
  readonly label: BufferGeometry | null;
  readonly width: number;
  readonly height: number;
  readonly thickness: number;
  /** Drop targets (types 1, 2 and 7) move down when they drop. */
  readonly isDrop: boolean;
}

/** True for the drop target types. */
export function isDropTarget(spec: TargetSpec): boolean {
  return spec.targetType === 1 || spec.targetType === 2 || spec.targetType === 7;
}

/** Thickness of the slab by type. */
function slabThickness(type: number): number {
  switch (type) {
    case 5:
    case 6:
      return 14;
    case 8:
      return 10;
    case 9:
      return 6;
    default:
      return 8;
  }
}

/** Build the geometry of a target. */
export function generateTarget(spec: TargetSpec): TargetGeometry {
  const size = spec.vSize ?? { x: 32, y: 32, z: 32 };
  const width = size.x;
  const height = size.z;
  const round = spec.targetType === 3;
  const thickness = round ? width : slabThickness(spec.targetType);

  let body: BufferGeometry;
  if (round) {
    body = new CylinderGeometry(width / 2, width / 2, height, 24);
    body.translate(0, height / 2, 0);
  } else {
    const bevel = spec.targetType === 1 ? 1.2 : 0;
    const w = width / 2 - bevel;
    const t = thickness / 2 - bevel;
    body = extrudeOutline(
      [
        { x: -w, y: -t },
        { x: w, y: -t },
        { x: w, y: t },
        { x: -w, y: t },
      ],
      Math.max(height - 2 * bevel, 1),
      bevel,
    );
    body.translate(0, bevel, 0);
  }
  body.name = `target.body-${spec.name}`;

  let label: BufferGeometry | null = null;
  if (spec.label !== undefined && spec.label !== "") {
    label = new PlaneGeometry(width * 0.8, height * 0.8);
    label.translate(0, height * 0.5, thickness / 2 + 0.3);
    label.name = `target.label-${spec.name}`;
  }
  return { body, label, width, height, thickness, isDrop: isDropTarget(spec) };
}
